import type { SupportedLng } from '../i18n';

/**
 * The eight shared demo accounts, from the reader's side.
 *
 * A visitor may look inside the portal before buying: /demo opens `demo-<lang>@solmiro.eu` for their
 * language — no password, no registration — and the session it hands back is read-only, enforced by the
 * server. Everything about that identity is fixed in code on both sides (there is no database field and
 * nothing to configure), so this module is the portal's copy of the two facts it needs: which languages
 * have a demo, and which city the installation behind each one sits in.
 *
 * Kept as plain data and pure functions, away from the components, so the language resolution and the
 * banner's city can be asserted without a DOM.
 */

/** City of the demo installation per language — mirrors `DemoAccounts.City` in sf.core. */
export const DEMO_CITIES: Record<SupportedLng, string> = {
  en: 'Amsterdam',
  nl: 'Utrecht',
  de: 'München',
  fr: 'Lyon',
  it: 'Bologna',
  es: 'Sevilla',
  da: 'Aarhus',
  pl: 'Wrocław',
};

/** The languages that have a demo account — the keys above, in one place so the two cannot disagree. */
export const DEMO_LANGS = Object.keys(DEMO_CITIES) as SupportedLng[];

/** "nl-NL" → "nl"; anything else → undefined. */
function normalise(value: string | undefined | null): SupportedLng | undefined {
  const base = (value ?? '').trim().slice(0, 2).toLowerCase();
  return (DEMO_LANGS as string[]).includes(base) ? (base as SupportedLng) : undefined;
}

/**
 * Which demo to open. `?lng=` wins — it is what the marketing site's "Try the demo" link carries, and it
 * is the language the visitor was reading in — with the language the portal is already showing as the
 * fallback, and English as the last resort (which is also what the API falls back to).
 */
export function demoLangFrom(
  search: string | undefined | null,
  current: string | undefined | null,
): SupportedLng {
  const asked = search ? new URLSearchParams(search).get('lng') : null;
  return normalise(asked) ?? normalise(current) ?? 'en';
}

/**
 * Does the session in this tab keep a visitor off a public page (/login, /register, /forgot-password,
 * /reset-password)?
 *
 * Only a real one does. A demo session is a look around, not an account, and for the hour it lives it must
 * not stand between a visitor and the page they came for: the marketing site's own "Create account" button
 * is a full page load straight to /register, and a password-reset link arrives from somebody's inbox. Bounce
 * either into the shared demo account and the visitor lands in the connect wizard, whose Connect button comes
 * back "this is the demo account" — the conversion path dead-ending in the read-only wall.
 */
export function blocksPublicRoute(
  status: 'loading' | 'authenticated' | 'anonymous',
  demo: boolean | undefined,
): boolean {
  return status === 'authenticated' && demo !== true;
}

/**
 * The city named on the demo banner. Read from the demo user's OWN locale, not from the interface
 * language: someone who opens the Danish demo and then switches the portal to English is still looking
 * at Aarhus.
 */
export function demoCityFor(locale: string | undefined | null): string {
  return DEMO_CITIES[normalise(locale) ?? 'en'];
}
